import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { InfoScreenCrossfade } from "@/pages/info-screen/components/InfoScreenCrossfade";
import { InfoScreenPageView } from "@/pages/info-screen/components/InfoScreenPageView";
import { InfoScreenState } from "@/pages/info-screen/components/InfoScreenState";
import {
  advancePageImageIndex,
  usesPageShowImageRotation,
} from "@/pages/info-screen/infoScreenImageRotation";
import type { InfoScreenContent } from "@/types/infoScreen";

type Props = {
  content: InfoScreenContent;
  /** Überlebt Modulwechsel (Seiten ↔ Veranstaltungen) auf dem Kiosk. */
  imageIndexByPageRef?: MutableRefObject<Record<string, number>>;
};

export function InfoScreenRotatingDisplay({ content, imageIndexByPageRef }: Props) {
  const pages = useMemo(
    () =>
      content.pages
        .filter((p) => p.enabled)
        .sort((a, b) => a.sort_order - b.sort_order),
    [content.pages],
  );

  const [pageIndex, setPageIndex] = useState(0);
  const rotationMs = Math.max(5, content.page_rotation_seconds) * 1000;
  const localImageIndexRef = useRef<Record<string, number>>({});
  const imageIndices = imageIndexByPageRef ?? localImageIndexRef;
  const [, setImageRotationTick] = useState(0);

  useEffect(() => {
    setPageIndex(0);
    imageIndices.current = {};
  }, [content.revision, imageIndices]);

  useEffect(() => {
    if (pages.length <= 1) return;
    const timer = window.setInterval(() => {
      setPageIndex((i) => (i + 1) % pages.length);
    }, rotationMs);
    return () => window.clearInterval(timer);
  }, [pages.length, rotationMs]);

  useEffect(() => {
    if (pageIndex >= pages.length) setPageIndex(0);
  }, [pageIndex, pages.length]);

  const activePage = pages[pageIndex] ?? pages[0];

  useEffect(() => {
    if (!activePage || !usesPageShowImageRotation(activePage)) return;
    const pageLeaving = activePage;
    return () => {
      advancePageImageIndex(imageIndices.current, pageLeaving);
    };
  }, [activePage?.id, pageIndex, imageIndices]);

  useEffect(() => {
    if (pages.length !== 1) return;
    const only = pages[0];
    if (!only || !usesPageShowImageRotation(only)) return;
    const intervalMs =
      only.layout === "columns"
        ? Math.max(3, only.slideshow_interval_seconds) * 1000
        : rotationMs;
    const timer = window.setInterval(() => {
      advancePageImageIndex(imageIndices.current, only);
      setImageRotationTick((t) => t + 1);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [pages, rotationMs, imageIndices]);

  if (!content.enabled) {
    return (
      <InfoScreenState
        title="Infobildschirm deaktiviert"
        subtitle="Bitte aktivieren Sie die Anzeige in den Stammdaten unter Infobildschirm."
      />
    );
  }

  if (pages.length === 0) {
    return (
      <InfoScreenState
        title="Keine Seiten konfiguriert"
        subtitle="Legen Sie in den Stammdaten mindestens eine aktive Informationsseite an."
      />
    );
  }

  if (!activePage) {
    return null;
  }

  const slideIndex = imageIndices.current[activePage.id] ?? 0;
  const isFullBleed =
    activePage.layout === "full_bleed" || activePage.layout === "image_focus";

  return (
    <div
      className={`info-screen-rotator${isFullBleed ? " info-screen-rotator--immersive" : ""}`}
      aria-live="polite"
    >
      <InfoScreenCrossfade
        itemKey={`${activePage.id}-${slideIndex}`}
        variant={isFullBleed ? "fade" : "slide-left"}
      >
        <InfoScreenPageView page={activePage} slideIndex={slideIndex} />
      </InfoScreenCrossfade>
      {pages.length > 1 ? (
        <div className="info-screen-page-indicator" aria-hidden>
          {pages.map((p, idx) => (
            <span
              key={p.id}
              className={
                idx === pageIndex
                  ? "info-screen-page-dot info-screen-page-dot--active"
                  : "info-screen-page-dot"
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
